/**
 * Unsaved changes, Discard, Save changes: one bar for the whole page, floating at the bottom.
 *
 * IT REPLACES THE SAVE IN THE HEADER, which covered the business form only and had to name
 * the tab it was talking about because four other things on the page saved themselves. Now
 * everything but the team and a tool's own Done waits for this, so the bar can say "Unsaved
 * changes" without a qualifier and the nav's dots say where.
 *
 * INK, FLOATING, WITH AN ACCENT SAVE, which are the two exceptions `styles.css` names: it sits
 * over whatever pane is open while that pane scrolls under it, so it is lifted with the one
 * shadow token, and its Save is the accent because an ink button on an ink bar is invisible.
 *
 * IT STAYS UP AFTER A SAVE THAT PARTLY FAILED, listing what did not go through with a Show
 * link to each, and it stays up briefly after a clean one with the receipt and anything
 * worth knowing - a greeting that could not be recorded, the clock recomputing - because
 * a bar that just vanished on success would leave those sentences nowhere. Briefly, notices
 * included: they once held the bar up until the next edit, which read as a save that never
 * finished (Illia, 2026-09-27). A notice gets longer on screen, enough to read a sentence.
 */

import { useEffect, useState } from "react";
import { cx } from "../../ui";
import { PART_LABEL, PART_SECTION, useDraft, type Part } from "./draft";
import type { SectionId } from "./sections";

/** How long the receipt stays after a clean save: long enough to read it, longer with a notice. */
const RECEIPT_MS = 5000;
const RECEIPT_WITH_NOTICE_MS = 12_000;

export function SaveBar({ hidden, onShow }: { hidden?: boolean; onShow: (id: SectionId) => void }) {
  const { dirty, state, status, save, discard } = useDraft();
  const errors = Object.entries(state.errors) as Array<[Part, string]>;
  const [receipt, setReceipt] = useState(false);

  useEffect(() => {
    if (!status.savedAt) return;
    setReceipt(true);
    const timer = setTimeout(() => setReceipt(false), status.notices.length ? RECEIPT_WITH_NOTICE_MS : RECEIPT_MS);
    return () => clearTimeout(timer);
  }, [status.savedAt, status.notices.length]);

  // A clean save's notices go with its receipt; a failed save's stay beside its errors.
  const notices = status.savedAt && !receipt ? [] : status.notices;
  const showing = dirty.length > 0 || errors.length > 0 || status.saving || (receipt && !dirty.length) || notices.length > 0;
  if (hidden || !showing) return null;

  return (
    <div
      role="region"
      aria-label="Save changes"
      className={cx(
        "fixed bottom-6 left-1/2 z-35 grid w-[min(760px,calc(100vw-32px))] -translate-x-1/2 gap-2 rounded-lg bg-ink px-5 py-2.5 text-paper shadow-float",
        "max-[720px]:bottom-0 max-[720px]:w-full max-[720px]:rounded-none max-[720px]:pb-[max(10px,env(safe-area-inset-bottom))]",
      )}
    >
      <div className="flex flex-wrap items-center gap-4.5">
        <span className="min-w-0 flex-1 text-sm" aria-live="polite">
          {status.saving ? (
            `Saving… (${Math.min(status.done + 1, status.total)} of ${status.total})`
          ) : dirty.length ? (
            <>
              Unsaved changes <span className="text-line-strong">apply from the next call once saved</span>
            </>
          ) : errors.length ? (
            "Some changes were not saved."
          ) : (
            "Saved. The next call uses it."
          )}
        </span>
        {dirty.length > 0 && (
          <>
            <button
              type="button"
              disabled={status.saving}
              onClick={discard}
              className="h-9 rounded-sm px-3.5 text-sm font-medium text-line-strong hover:text-paper disabled:opacity-55"
            >
              Discard
            </button>
            <button
              type="button"
              disabled={status.saving}
              onClick={() => void save()}
              className="h-9 rounded-sm bg-accent px-4 text-sm font-medium text-accent-ink hover:bg-accent-hover disabled:opacity-55"
            >
              {status.saving ? "Saving…" : "Save changes"}
            </button>
          </>
        )}
      </div>
      {errors.length > 0 && !status.saving && (
        <ul role="alert" className="grid gap-1 border-t border-ink-soft pt-2 text-xs">
          {errors.map(([part, message]) => (
            <li key={part} className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-semibold text-paper">Couldn't save {PART_LABEL[part]}:</span>
              <span className="text-line-strong">{message}</span>
              <button type="button" className="font-medium text-accent-line underline-offset-2 hover:underline" onClick={() => onShow(PART_SECTION[part])}>
                Show
              </button>
            </li>
          ))}
        </ul>
      )}
      {notices.length > 0 && !status.saving && (
        <ul className="grid gap-1 border-t border-ink-soft pt-2 text-xs text-line-strong">
          {notices.map((notice) => (
            <li key={notice}>{notice}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
